import classNames from 'classnames';
import React, { Component } from 'react';
import Button, { ButtonProps } from '../Button';

export interface TableState {
  /**
   * 横スクロールのフェードを表示するかどうか
   */
  showFade: boolean;

  /**
   * 横スクロールが発生するかどうか
   */
  hasScroll: boolean;
}

export interface TableProps {
  className?: string;
  /**
   * 左側のボタンアイコン. MaterialIconsのアイコン名が入ります。
   * https://material.io/resources/icons
   */

  /**
   * テーブルのタイプ. デフォルトは `normal`
   */
  type?: 'normal' | 'striped' | 'border';

  /**
   * ホバーあり
   */
  isHoverable?: boolean;

  /**
   * 横スクロールあり
   */
  isScrollable?: boolean;

  /**
   * 高さ. デフォルトは `m`
   */
  height?: 'm' | 's';

  /**
   * テーブルの最小幅
   * isScrollable=true のとき
   */
  minWidth?: string;

  /**
   * 横スクロールのフェードを表示するかどうか
   */
  hasFade?: boolean;

  /**
   * 強制適用class;classNameで設定されるclassを上書きする
   */
  classNameImportant?: string;
}

class Table extends Component<TableProps, TableState> {
  static THead: typeof TableHead;
  static TBody: typeof TableBody;
  static TFoot: typeof TableFoot;
  static Tr: typeof TableTr;
  static Th: typeof TableTh;
  static ThButton: typeof TableThButton;
  static Td: typeof TableTd;

  private tableRef: React.RefObject<HTMLDivElement>;
  private tableInnerRef: React.RefObject<HTMLTableElement>;

  constructor(props: TableProps) {
    super(props);

    this.state = {
      showFade: true,
      hasScroll: true,
    };

    this.tableRef = React.createRef<HTMLDivElement>();
    this.tableInnerRef = React.createRef<HTMLTableElement>();

    this.onWindowResize = this.onWindowResize.bind(this);
    this.onScroll = this.onScroll.bind(this);
  }

  componentDidMount() {
    const { isScrollable, hasFade = true } = this.props;
    if (isScrollable && hasFade) {
      window.addEventListener('resize', this.onWindowResize);

      this.onWindowResize();
      this.onScroll();
    }
  }

  componentWillUnmount() {
    const { isScrollable, hasFade = true } = this.props;
    if (isScrollable && hasFade) {
      window.removeEventListener('resize', this.onWindowResize);
    }
  }

  /**
   * ウィンドウがリサイズしたときに呼ばれる
   * テーブルサイズが変わる可能性があるため、hasScrollを更新する
   */
  onWindowResize() {
    const table = this.tableRef.current; // this.refs.table as HTMLElement;
    const table_inner = this.tableInnerRef.current; // this.refs.table_inner as HTMLElement;

    if (!table || !table_inner) return;

    this.setState({
      hasScroll: table.clientWidth !== table_inner.clientWidth,
    });
  }

  /**
   * スクロールした時に、右側フェードを表示するかどうかを判定する
   */
  onScroll() {
    const { isScrollable, hasFade = true } = this.props;
    if (!isScrollable || !hasFade) return;

    const { hasScroll } = this.state;
    const table = this.tableRef.current; // this.refs.table as HTMLElement;

    if (!table) return;

    const { scrollLeft, clientWidth, scrollWidth } = table;

    this.setState({
      showFade: scrollLeft + clientWidth !== scrollWidth && hasScroll,
    });
  }

  render() {
    const { showFade } = this.state;
    const { type, isHoverable, isScrollable, height, className, minWidth, hasFade = true, children, classNameImportant } = this.props;
    const classes = classNames(
      'uc-table',
      !isScrollable && className,
      type && ['striped', 'border'].includes(type) && `uc-table--${type}`,
      isHoverable && 'uc-table--hoverable',
      height === 's' && 'uc-table--hs'
    );

    const renderTable = (
      <div className={classNameImportant ? classNameImportant : classes} ref={this.tableRef} onScroll={this.onScroll}>
        <table style={minWidth !== undefined ? { width: '100%', minWidth: minWidth } : {}} ref={this.tableInnerRef}>
          {children}
        </table>
      </div>
    );

    return isScrollable ? (
      <div className={classNames('uc-scroll-table', className)}>
        {renderTable}
        {hasFade && <div className={classNames('uc-table__fade', !showFade && 'uc-table__fade--hidden')}></div>}
      </div>
    ) : (
      renderTable
    );
  }
}

/**
 * <thead>
 * Table.THead
 */
const TableHead: React.FC<React.HTMLProps<HTMLTableSectionElement>> = ({ children, ...props }) => <thead {...props}>{children}</thead>;

/**
 * <tbody>
 * Table.TBody
 */
const TableBody: React.FC<React.HTMLProps<HTMLTableSectionElement>> = ({ children, ...props }) => <tbody {...props}>{children}</tbody>;

/**
 * <tfoot>
 * Table.Tfoot
 */
const TableFoot: React.FC<React.HTMLProps<HTMLTableSectionElement>> = ({ children, ...props }) => <tfoot {...props}>{children}</tfoot>;

/**
 * <tr>
 * Table.Tr
 */
interface TableRowProps extends React.HTMLProps<HTMLTableRowElement> {}

const TableTr: React.FC<TableRowProps> = ({ children, ...props }) => <tr {...props}>{children}</tr>;

/**
 * <th>
 * Table.Th
 */
interface TableHeadProps extends React.HTMLProps<HTMLTableHeaderCellElement> {}

const TableTh: React.FC<TableHeadProps> = ({ children, ...props }) => <th {...props}>{children}</th>;

interface TableThButtonProps extends ButtonProps {
  /**
   * ボタン(並び替えや絞り込み)がアクティブ状態の時
   */
  isActive?: boolean;
}

const TableThButton: React.FC<TableThButtonProps> = ({ className, children, isActive, ...props }) => {
  const classes = classNames(className, 'uc-table__sort-button', isActive && 'uc-table__sort-button--active');
  return <Button className={classes} {...props} />;
};

/**
 * <td>
 * Table.Td
 */
interface TableDataProps extends React.HTMLProps<HTMLTableDataCellElement> {
  isEditCell?: boolean;
}

const TableTd: React.FC<TableDataProps> = ({ className, isEditCell, children, ...props }) => {
  const classes = classNames(className, isEditCell && 'uc-table__edit-cell');
  return (
    <td className={classes} {...props}>
      {children}
    </td>
  );
};

Table.THead = TableHead;
Table.TBody = TableBody;
Table.TFoot = TableFoot;
Table.Tr = TableTr;
Table.Th = TableTh;
Table.ThButton = TableThButton;
Table.Td = TableTd;

export default Table;
